import { useMemo, useState } from "react";
import {
    AlertCircle,
    ArrowDown,
    ChevronDown,
    Flag,
    Globe2,
    Map as MapIcon,
    Target,
    TrendingUp,
    Users,
} from "lucide-react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useCountries } from "../api/queries";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { Segmented } from "../components/Segmented";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { fmt, fmtPct } from "../lib/format";
import { cn } from "@/lib/utils";
import type { CountryEntry } from "../types/reports";

// Mapa de códigos ISO-2 → nome PT-BR + flag emoji
// Hardcoded pros principais; fallback: converte ISO em flag via regional indicators.
const COUNTRY_INFO: Record<string, { name: string; flag: string }> = {
    BR: { name: "Brasil", flag: "🇧🇷" },
    US: { name: "EUA", flag: "🇺🇸" },
    PT: { name: "Portugal", flag: "🇵🇹" },
    AR: { name: "Argentina", flag: "🇦🇷" },
    MX: { name: "México", flag: "🇲🇽" },
    CO: { name: "Colômbia", flag: "🇨🇴" },
    ES: { name: "Espanha", flag: "🇪🇸" },
    CL: { name: "Chile", flag: "🇨🇱" },
    PE: { name: "Peru", flag: "🇵🇪" },
    UY: { name: "Uruguai", flag: "🇺🇾" },
    PY: { name: "Paraguai", flag: "🇵🇾" },
    BO: { name: "Bolívia", flag: "🇧🇴" },
    EC: { name: "Equador", flag: "🇪🇨" },
    VE: { name: "Venezuela", flag: "🇻🇪" },
    DO: { name: "Rep. Dominicana", flag: "🇩🇴" },
    CR: { name: "Costa Rica", flag: "🇨🇷" },
    PA: { name: "Panamá", flag: "🇵🇦" },
    GT: { name: "Guatemala", flag: "🇬🇹" },
    HN: { name: "Honduras", flag: "🇭🇳" },
    SV: { name: "El Salvador", flag: "🇸🇻" },
    NI: { name: "Nicarágua", flag: "🇳🇮" },
    CA: { name: "Canadá", flag: "🇨🇦" },
    GB: { name: "Reino Unido", flag: "🇬🇧" },
    FR: { name: "França", flag: "🇫🇷" },
    DE: { name: "Alemanha", flag: "🇩🇪" },
    IT: { name: "Itália", flag: "🇮🇹" },
    NL: { name: "Holanda", flag: "🇳🇱" },
    BE: { name: "Bélgica", flag: "🇧🇪" },
    CH: { name: "Suíça", flag: "🇨🇭" },
    AT: { name: "Áustria", flag: "🇦🇹" },
    SE: { name: "Suécia", flag: "🇸🇪" },
    NO: { name: "Noruega", flag: "🇳🇴" },
    FI: { name: "Finlândia", flag: "🇫🇮" },
    DK: { name: "Dinamarca", flag: "🇩🇰" },
    IE: { name: "Irlanda", flag: "🇮🇪" },
    AU: { name: "Austrália", flag: "🇦🇺" },
    NZ: { name: "Nova Zelândia", flag: "🇳🇿" },
    JP: { name: "Japão", flag: "🇯🇵" },
    CN: { name: "China", flag: "🇨🇳" },
    KR: { name: "Coreia do Sul", flag: "🇰🇷" },
    IN: { name: "Índia", flag: "🇮🇳" },
    RU: { name: "Rússia", flag: "🇷🇺" },
    ZA: { name: "África do Sul", flag: "🇿🇦" },
    NG: { name: "Nigéria", flag: "🇳🇬" },
    EG: { name: "Egito", flag: "🇪🇬" },
    AE: { name: "Emirados Árabes", flag: "🇦🇪" },
    SA: { name: "Arábia Saudita", flag: "🇸🇦" },
    TR: { name: "Turquia", flag: "🇹🇷" },
    IL: { name: "Israel", flag: "🇮🇱" },
};

function countryFlag(code: string | null | undefined): string {
    if (!code) return "🏳";
    const upper = code.toUpperCase();
    if (COUNTRY_INFO[upper]) return COUNTRY_INFO[upper].flag;
    if (upper.length !== 2) return "🏳";
    // Convert ISO-2 → regional indicator emoji
    return String.fromCodePoint(
        ...upper.split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
    );
}

function countryName(code: string, fallback?: string): string {
    const upper = (code || "").toUpperCase();
    if (COUNTRY_INFO[upper]) return COUNTRY_INFO[upper].name;
    return fallback || upper || "—";
}

const WORLD_GEO_URL =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

type MapMetric = "revenue" | "rpm";

// `world-atlas/countries-110m.json` (TopoJSON) usa `geo.id` no formato ISO
// 3166-1 NUMERIC ("076" pro Brasil, "840" pros US) — NÃO ISO-3 string. Sem
// essa tabela o mapa todo ficava cinza porque `ISO3_TO_ISO2["076"]` é undef.
// Lista cobre os países que aparecem no `revenue_facts.country` em prod.
const NUMERIC_TO_ISO2: Record<string, string> = {
    "032": "AR", "036": "AU", "040": "AT", "056": "BE", "068": "BO",
    "076": "BR", "124": "CA", "152": "CL", "156": "CN", "170": "CO",
    "188": "CR", "192": "CU", "203": "CZ", "208": "DK", "214": "DO",
    "218": "EC", "222": "SV", "246": "FI", "250": "FR", "276": "DE",
    "300": "GR", "320": "GT", "340": "HN", "348": "HU", "352": "IS",
    "356": "IN", "372": "IE", "376": "IL", "380": "IT", "392": "JP",
    "410": "KR", "440": "LT", "442": "LU", "484": "MX", "528": "NL",
    "554": "NZ", "558": "NI", "566": "NG", "578": "NO", "591": "PA",
    "600": "PY", "604": "PE", "608": "PH", "616": "PL", "620": "PT",
    "624": "GW", "630": "PR", "634": "QA", "642": "RO", "643": "RU",
    "682": "SA", "688": "RS", "702": "SG", "703": "SK", "705": "SI",
    "710": "ZA", "724": "ES", "752": "SE", "756": "CH", "764": "TH",
    "784": "AE", "792": "TR", "804": "UA", "818": "EG", "826": "GB",
    "840": "US", "858": "UY", "862": "VE", "887": "YE", "894": "ZM",
};

// Mapeia ISO-3 (algumas fontes alternativas) pra ISO-2 (que vem da API).
// Mantemos como fallback além do NUMERIC_TO_ISO2.
const ISO3_TO_ISO2: Record<string, string> = {
    BRA: "BR",
    USA: "US",
    PRT: "PT",
    ARG: "AR",
    MEX: "MX",
    COL: "CO",
    ESP: "ES",
    CHL: "CL",
    PER: "PE",
    URY: "UY",
    PRY: "PY",
    BOL: "BO",
    ECU: "EC",
    VEN: "VE",
    DOM: "DO",
    CRI: "CR",
    PAN: "PA",
    GTM: "GT",
    HND: "HN",
    SLV: "SV",
    NIC: "NI",
    CAN: "CA",
    GBR: "GB",
    FRA: "FR",
    DEU: "DE",
    ITA: "IT",
    NLD: "NL",
    BEL: "BE",
    CHE: "CH",
    AUT: "AT",
    SWE: "SE",
    NOR: "NO",
    FIN: "FI",
    DNK: "DK",
    IRL: "IE",
    AUS: "AU",
    NZL: "NZ",
    JPN: "JP",
    CHN: "CN",
    KOR: "KR",
    IND: "IN",
    RUS: "RU",
    ZAF: "ZA",
    NGA: "NG",
    EGY: "EG",
    ARE: "AE",
    SAU: "SA",
    TUR: "TR",
    ISR: "IL",
};

// hex interpolation #dbeafe → #1d4ed8
function colorMix(t: number): string {
    const c1 = [219, 234, 254];
    const c2 = [29, 78, 216];
    const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
    const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
    const b = Math.round(c1[2] + (c2[2] - c1[2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
}

export function CountriesView() {
    const { data, error, isLoading, refetch } = useCountries();
    const loading = isLoading && !data;

    const [mapMetric, setMapMetric] = useState<MapMetric>("revenue");
    const [showAll, setShowAll] = useState(false);
    const [showConv, setShowConv] = useState(false);

    const hero = (
        <PageHeader
            icon={Globe2}
            title="Países"
            subtitle="Distribuição geográfica de receita, RPM e conversão"
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const items: CountryEntry[] = data?.items ?? [];
    const total = data?.total;

    // Aug items with share + rank
    const itemsAug = useMemo(() => {
        if (!items.length) return [];
        const sorted = [...items].sort((a, b) => b.revenue - a.revenue);
        const sum = sorted.reduce((a, c) => a + (c.revenue || 0), 0) || 1;
        return sorted.map((c, i) => ({
            ...c,
            rank: i + 1,
            sharePct: ((c.revenue || 0) / sum) * 100,
        }));
    }, [items]);

    // KPIs (derive locally since API doesn't expose them explicitly)
    const activeCountries = itemsAug.length;
    const topCountry = itemsAug[0];
    const highestRpm = useMemo(() => {
        if (!itemsAug.length) return null;
        return [...itemsAug]
            .filter((c) => (c.rpm ?? 0) > 0)
            .sort((a, b) => (b.rpm ?? 0) - (a.rpm ?? 0))[0];
    }, [itemsAug]);
    const totalRevenue =
        total?.revenue ??
        itemsAug.reduce((a, c) => a + (c.revenue || 0), 0);
    const top10Coverage =
        totalRevenue > 0
            ? (itemsAug.slice(0, 10).reduce((a, c) => a + c.revenue, 0) /
                  totalRevenue) *
              100
            : 0;

    const visibleItems = showAll ? itemsAug : itemsAug.slice(0, 10);
    const hiddenCount = Math.max(0, itemsAug.length - 10);

    return (
        <PageShell hero={hero}>
            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <KpiCard
                    icon={Globe2}
                    iconColor="text-blue-500"
                    label="Países ativos"
                    value={loading ? <SkelText /> : fmt(activeCountries)}
                    sub="no período"
                />
                <KpiCard
                    icon={Flag}
                    iconColor="text-emerald-500"
                    label="Top país"
                    value={
                        loading ? (
                            <SkelText />
                        ) : topCountry ? (
                            `${countryFlag(topCountry.code)} ${countryName(topCountry.code, topCountry.name)}`
                        ) : (
                            "—"
                        )
                    }
                    sub={
                        topCountry
                            ? `$${Number(topCountry.revenue).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} · ${topCountry.sharePct.toFixed(1)}%`
                            : ""
                    }
                />
                <KpiCard
                    icon={TrendingUp}
                    iconColor="text-amber-500"
                    label="Maior RPM"
                    value={
                        loading ? (
                            <SkelText />
                        ) : highestRpm ? (
                            `${countryFlag(highestRpm.code)} ${countryName(highestRpm.code, highestRpm.name)}`
                        ) : (
                            "—"
                        )
                    }
                    sub={
                        highestRpm
                            ? `$${(highestRpm.rpm ?? 0).toFixed(2)}`
                            : ""
                    }
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-violet-500"
                    label="Cobertura top10"
                    value={loading ? <SkelText /> : fmtPct(top10Coverage)}
                    sub="da receita total"
                />
            </div>

            {/* Map + ranking */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <SectionCard
                    icon={MapIcon}
                    title="Mapa mundial"
                    subtitle={
                        mapMetric === "revenue"
                            ? "intensidade = receita"
                            : "intensidade = RPM"
                    }
                    className="lg:col-span-3"
                    right={
                        <Segmented<MapMetric>
                            value={mapMetric}
                            onChange={setMapMetric}
                            options={[
                                { v: "revenue", l: "Receita" },
                                { v: "rpm", l: "RPM" },
                            ]}
                        />
                    }
                >
                    {loading ? (
                        <DashSkeleton h={420} />
                    ) : (
                        <WorldChoropleth items={itemsAug} metric={mapMetric} />
                    )}
                </SectionCard>

                <SectionCard
                    icon={TrendingUp}
                    title="Ranking · receita"
                    subtitle={
                        itemsAug.length ? `${itemsAug.length} países` : ""
                    }
                    className="lg:col-span-2"
                    right={
                        <button
                            onClick={() => setShowConv((v) => !v)}
                            className={cn(
                                "inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg border transition-colors",
                                showConv
                                    ? "bg-violet-50 border-violet-200 text-violet-700"
                                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                            )}
                        >
                            <Target className="w-3 h-3" />
                            Conversão
                        </button>
                    }
                >
                    {loading ? (
                        <DashSkeleton h={400} />
                    ) : itemsAug.length === 0 ? (
                        <EmptyMsg />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase text-gray-500 tracking-wider border-b border-gray-200">
                                        <th className="text-left py-2">#</th>
                                        <th className="text-left py-2">País</th>
                                        <th className="text-right py-2">$</th>
                                        <th className="text-right py-2">
                                            %tot
                                        </th>
                                        <th className="text-right py-2">RPM</th>
                                        {showConv && (
                                            <th className="text-right py-2">
                                                conv
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {visibleItems.map((c) => {
                                        const hideConvVal =
                                            showConv &&
                                            (c.conv == null || c.conv === 0);
                                        return (
                                            <tr
                                                key={c.code}
                                                className="hover:bg-gray-50"
                                            >
                                                <td className="py-2 text-xs text-gray-400 font-mono">
                                                    {String(c.rank).padStart(
                                                        2,
                                                        "0"
                                                    )}
                                                </td>
                                                <td className="py-2">
                                                    <div className="flex items-center gap-2">
                                                        <span>
                                                            {countryFlag(c.code)}
                                                        </span>
                                                        <span
                                                            className={
                                                                c.rank === 1
                                                                    ? "font-semibold text-gray-900"
                                                                    : "text-gray-700"
                                                            }
                                                        >
                                                            {countryName(
                                                                c.code,
                                                                c.name
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-2 text-right font-semibold text-gray-900 font-mono">
                                                    $
                                                    {Number(
                                                        c.revenue
                                                    ).toLocaleString("pt-BR", {
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </td>
                                                <td className="py-2 text-right text-xs text-gray-500 font-mono">
                                                    {c.sharePct.toFixed(1)}%
                                                </td>
                                                <td className="py-2 text-right text-xs font-mono text-blue-600">
                                                    ${(c.rpm ?? 0).toFixed(2)}
                                                </td>
                                                {showConv && (
                                                    <td className="py-2 text-right text-xs font-mono text-gray-700">
                                                        {hideConvVal
                                                            ? "—"
                                                            : (c.conv ?? 0).toFixed(
                                                                  1
                                                              ) + "%"}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {hiddenCount > 0 && (
                                <button
                                    onClick={() => setShowAll((v) => !v)}
                                    className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 py-2 rounded-lg transition-colors"
                                >
                                    {showAll ? (
                                        <>
                                            <ChevronDown className="w-3.5 h-3.5 rotate-180" />
                                            Mostrar top 10
                                        </>
                                    ) : (
                                        <>
                                            <ArrowDown className="w-3.5 h-3.5" />
                                            Ver mais {hiddenCount} países
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </SectionCard>
            </div>
        </PageShell>
    );
}

// ── Choropleth world map ────────────────────────────────────
interface CountryAug extends CountryEntry {
    rank: number;
    sharePct: number;
}

interface WorldChoroplethProps {
    items: CountryAug[];
    metric: MapMetric;
}

interface HoverInfo {
    code: string;
    name: string;
    value: number;
    x: number;
    y: number;
}

function WorldChoropleth({ items, metric }: WorldChoroplethProps) {
    const [hover, setHover] = useState<HoverInfo | null>(null);

    // Build value lookup by ISO-2 code
    const valueByCode = useMemo(() => {
        const map = new Map<string, { value: number; name: string }>();
        for (const c of items) {
            const code = (c.code || "").toUpperCase();
            if (!code) continue;
            const value = metric === "rpm" ? c.rpm ?? 0 : c.revenue ?? 0;
            map.set(code, {
                value,
                name: countryName(code, c.name),
            });
        }
        return map;
    }, [items, metric]);

    // Log scale
    const { lo, hi } = useMemo(() => {
        const logs = Array.from(valueByCode.values())
            .filter((v) => v.value > 0)
            .map((v) => Math.log10(v.value + 1));
        if (!logs.length) return { lo: 0, hi: 1 };
        return { lo: Math.min(...logs), hi: Math.max(...logs) };
    }, [valueByCode]);

    function fillFor(value: number | undefined): string {
        if (!value || value <= 0) return "#f3f4f6";
        const t = hi > lo ? (Math.log10(value + 1) - lo) / (hi - lo) : 1;
        return colorMix(0.15 + t * 0.85);
    }

    const vals = Array.from(valueByCode.values()).map((v) => v.value).filter((v) => v > 0);
    const minV = vals.length ? Math.min(...vals) : 0;
    const maxV = vals.length ? Math.max(...vals) : 0;
    const fmtLeg = (v: number) =>
        metric === "rpm"
            ? "$" + v.toFixed(2)
            : v >= 1000
              ? "$" + (v / 1000).toFixed(1) + "k"
              : "$" + v.toFixed(0);
    const fmtVal = (v: number) =>
        metric === "rpm"
            ? "RPM $" + v.toFixed(2)
            : "$" +
              Number(v).toLocaleString("pt-BR", {
                  maximumFractionDigits: 2,
              });

    return (
        <div className="relative">
            <div className="rounded-xl overflow-hidden bg-slate-50" style={{ height: 420 }}>
                <ComposableMap
                    projectionConfig={{ scale: 140 }}
                    width={800}
                    height={420}
                    style={{ width: "100%", height: "100%" }}
                >
                    <Geographies geography={WORLD_GEO_URL}>
                        {({ geographies }: { geographies: GeoFeature[] }) =>
                            geographies.map((geo) => {
                                // `geo.id` no world-atlas é ISO 3166-1 NUMERIC
                                // ("076" pra BR, "840" pros US); outros geos
                                // (ex: natural-earth via topojson) entregam
                                // ISO-3 em properties.iso_a3. Tentamos os dois.
                                const rawId = String(geo.id ?? "");
                                const iso3 =
                                    geo.properties?.iso_a3 ||
                                    geo.properties?.ISO_A3 ||
                                    "";
                                const iso2 =
                                    NUMERIC_TO_ISO2[rawId] ||
                                    ISO3_TO_ISO2[iso3] ||
                                    (geo.properties?.iso_a2 ||
                                        geo.properties?.ISO_A2 ||
                                        "")
                                        .toString()
                                        .toUpperCase();
                                const entry = iso2
                                    ? valueByCode.get(iso2)
                                    : undefined;
                                const value = entry?.value;
                                const fill = fillFor(value);
                                return (
                                    <Geography
                                        key={geo.rsmKey ?? iso3}
                                        geography={geo}
                                        fill={fill}
                                        stroke="#ffffff"
                                        strokeWidth={0.4}
                                        onMouseEnter={(e) => {
                                            if (!entry) return;
                                            setHover({
                                                code: iso2,
                                                name: entry.name,
                                                value: entry.value,
                                                x: e.clientX,
                                                y: e.clientY,
                                            });
                                        }}
                                        onMouseMove={(e) => {
                                            if (!entry) return;
                                            setHover((prev) =>
                                                prev
                                                    ? {
                                                          ...prev,
                                                          x: e.clientX,
                                                          y: e.clientY,
                                                      }
                                                    : null
                                            );
                                        }}
                                        onMouseLeave={() => setHover(null)}
                                        style={{
                                            default: {
                                                outline: "none",
                                                transition: "fill 120ms",
                                            },
                                            hover: {
                                                outline: "none",
                                                fill: entry
                                                    ? fill
                                                    : "#e5e7eb",
                                                opacity: 0.85,
                                                cursor: entry
                                                    ? "pointer"
                                                    : "default",
                                            },
                                            pressed: { outline: "none" },
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>
            </div>

            {/* Tooltip */}
            {hover && (
                <div
                    className="fixed z-50 pointer-events-none bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg"
                    style={{
                        left: hover.x + 12,
                        top: hover.y + 12,
                    }}
                >
                    <span className="mr-1">{countryFlag(hover.code)}</span>
                    <span className="font-semibold">{hover.name}</span>
                    <span className="ml-1.5 font-mono">
                        {fmtVal(hover.value)}
                    </span>
                </div>
            )}

            {/* Legend */}
            <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400 font-mono">
                        {fmtLeg(minV)}
                    </span>
                    <div
                        className="h-2.5 w-40 rounded-full"
                        style={{
                            background:
                                "linear-gradient(90deg, #dbeafe, #1d4ed8)",
                        }}
                    />
                    <span className="text-[10px] text-gray-400 font-mono">
                        {fmtLeg(maxV)}
                    </span>
                </div>
                <span className="text-[10px] text-gray-400">
                    escala log · {metric === "rpm" ? "RPM" : "receita"} por país
                </span>
            </div>
        </div>
    );
}

// react-simple-maps types Geographies' children arg with `any` — we narrow loosely.
interface GeoFeature {
    rsmKey?: string;
    id?: string;
    properties?: Record<string, unknown> & {
        iso_a2?: string;
        ISO_A2?: string;
        iso_a3?: string;
        ISO_A3?: string;
    };
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados no período</p>
        </div>
    );
}
